import { ConfigOptions, FeatureConfig, PlannerConfig } from 'rangeConfig/@types';
import platsaCatalog from '@inter-ikea-storageone/range-platsa/data/catalog.json';
import { platsaComponentResolver } from '@inter-ikea-storageone/range-platsa';
import { plannerComponentResolver } from '@inter-ikea-storageone/range';
import platsaTemplate from '@inter-ikea-storageone/range-platsa/data/template.json';
import proppingCatalog from '@inter-ikea-storageone/range-bedroom-storage-propping/data/catalog.json';
import proppingTemplate from '@inter-ikea-storageone/range-bedroom-storage-propping/data/template.json';
import handlesCatalog from '@inter-ikea-storageone/range-handles/data/catalog.json';
import handlesTemplate from '@inter-ikea-storageone/range-handles/data/template.json';
import platsaInteriorSuggestionsCatalog from '@inter-ikea-storageone/range-platsa/data/interior-suggestions/catalog.json';
import platsaInteriorSuggestionsTemplate from '@inter-ikea-storageone/range-platsa/data/interior-suggestions/template.json';
import platsaFrontLayoutsCatalog from '@inter-ikea-storageone/range-platsa/data/front-layouts/catalog.json';
import platsaFrontLayoutsTemplate from '@inter-ikea-storageone/range-platsa/data/front-layouts/template.json';
import frames from './productGroups/frames';
import PlatsaCameraBehavior from './camera/PlatsaCameraBehavior';
import interiors from './productGroups/interiors';
import fronts from './productGroups/fronts';
import handles from './productGroups/handles';
import legs from './productGroups/legs';
import { RecommendHandles } from 'rangeConfig/shared/features/RecommendHandles/RecommendHandles';
import { ToggleDoorsFeature as ToggleDoors } from 'rangeConfig/shared/features/ToggleHingeDoors';
import platform from 'rangeConfig/shared/utils/platform';
import { HelpLinesFeature } from 'rangeConfig/shared/features/HelpLines/HelpLinesFeature';
import { getOptimizedModels } from 'rangeConfig/shared/utils/optimizedModels';
import ProppingAction from 'components/SceneMenu/actions/ProppingAction';
import MeasureAction from 'components/SceneMenu/actions/MeasureAction';
import DoorsAction from 'components/SceneMenu/actions/DoorsAction';
import { HandlesDrillTemplate } from 'rangeConfig/shared/features/HandlesDrillTemplate/HandlesDrillTemplate';
import sprToVpcMap from '@inter-ikea-storageone/range-platsa/data/spr2vpc.json';
import accessories from './productGroups/accessories';
import { SoftClosingHingesFeature } from 'rangeConfig/platsa/features/SoftClosingHinges/SoftClosingHingesFeature';
import { TopInfoFeature } from 'rangeConfig/shared/features/TopInfo/TopInfoFeature';
import { TopFrameWidgetInfoFeature } from 'rangeConfig/shared/features/TopFrameWidgetInfo/TopFrameWidgetInfoFeature';
import platsaLandingPageConfig from './platsaLandingPageConfig';
import lights from './productGroups/lights';
import { NightLightsFeature } from 'rangeConfig/shared/features/NightLights/NightLightsFeature';
import { TooManyLightsFeature } from 'rangeConfig/shared/features/TooManyLights/TooManyLightsFeature';
import { ClothHangersFeature } from 'rangeConfig/shared/features/ClothHangers/ClothHangersFeature';
import { Autofill } from 'rangeConfig/platsa/features/Autofill/Autofill';
import { DistanceMeasurementFeature } from 'rangeConfig/shared/features/DistanceMeasurementFeature';
import { MultiWallSeeThroughFeature } from 'rangeConfig/shared/features/MultiWallSeeThroughFeature';

const defaultAssetsPath = '/assets/';
const assetsPath = import.meta.env.REACT_APP_ASSETS_PATH || defaultAssetsPath;
const screensaverPath = `${assetsPath}screensaver/screensaver_PLATSA_no_logo.mp4`;

const platsaRange = (options?: ConfigOptions): PlannerConfig => {
  const productGroups = [frames, fronts, handles, interiors, accessories, legs, lights].map(g =>
    g(options)
  );

  const features: { mobile: FeatureConfig[]; desktop: FeatureConfig[] } = {
    mobile: [
      { id: 'MultiwallSeeThroughFeature', feature: MultiWallSeeThroughFeature },
      { id: 'ToggleDoors', feature: ToggleDoors },
      { id: 'RecommendHandles', feature: RecommendHandles },
      { id: 'HandlesDrillTemplate', feature: HandlesDrillTemplate },
      { id: 'SoftClosingHinges', feature: SoftClosingHingesFeature },
      { id: 'TopInfo', feature: TopInfoFeature },
      { id: 'TopFrameWidgetInfo', feature: TopFrameWidgetInfoFeature },
      { id: 'TooManyLights', feature: TooManyLightsFeature }
    ],
    desktop: [
      { id: 'MultiwallSeeThroughFeature', feature: MultiWallSeeThroughFeature },
      { id: 'RecommendHandles', feature: RecommendHandles },
      { id: 'ToggleDoors', feature: ToggleDoors },
      { id: 'HelpLines', feature: HelpLinesFeature },
      { id: 'HandlesDrillTemplate', feature: HandlesDrillTemplate },
      { id: 'SoftClosingHinges', feature: SoftClosingHingesFeature },
      { id: 'TopInfo', feature: TopInfoFeature },
      { id: 'TopFrameWidgetInfo', feature: TopFrameWidgetInfoFeature },
      { id: 'NightLights', feature: NightLightsFeature },
      { id: 'TooManyLights', feature: TooManyLightsFeature },
      { id: 'Autofill', feature: Autofill },
      { id: 'DistanceMeasurement', feature: DistanceMeasurementFeature }
    ]
  };

  if (options?.featureFlags?.bumerangHangers) {
    features.mobile.push({ id: 'ClothHangersFeature', feature: ClothHangersFeature });
    features.desktop.push({ id: 'ClothHangersFeature', feature: ClothHangersFeature });
  }

  const sceneActions = {
    desktop: [MeasureAction, DoorsAction, ProppingAction],
    mobile: [DoorsAction, MeasureAction]
  };

  return {
    productCatalogs: [
      {
        name: 'platsa',
        catalog: getOptimizedModels(platsaCatalog),
        template: platsaTemplate,
        assetsPath
      },
      {
        name: 'platsa-interior-suggestions',
        catalog: platsaInteriorSuggestionsCatalog,
        template: platsaInteriorSuggestionsTemplate,
        assetsPath
      },
      {
        name: 'platsa-front-layouts',
        catalog: platsaFrontLayoutsCatalog,
        template: platsaFrontLayoutsTemplate,
        assetsPath
      },
      {
        name: 'handles',
        catalog: getOptimizedModels(handlesCatalog),
        template: handlesTemplate,
        assetsPath
      },
      {
        name: 'propping',
        catalog: getOptimizedModels(proppingCatalog),
        template: proppingTemplate,
        assetsPath
      }
    ],
    sprToVpcMap,
    componentResolvers: [plannerComponentResolver, platsaComponentResolver],
    featureToggles: [],
    productGroups,
    screensaverPath: screensaverPath,
    cameraBehavior: PlatsaCameraBehavior,
    features: features[platform],
    sceneActions: sceneActions[platform],
    roomOptions: { dimensions: { width: 5000, height: 3000, depth: 4000 } },
    landingPageConfig: platsaLandingPageConfig
  };
};

export default platsaRange;
